import React, { useState } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import { Button } from "./ui/button";
import { Move, Check, X } from "lucide-react";
import { toast } from "sonner";

const pinIcon = L.divIcon({
  className: "custom-marker",
  html: `<div style="background-color: #F59E0B; width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3); border: 3px solid white;"><div style="width: 8px; height: 8px; background: white; border-radius: 50%;"></div></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

// Small map preview showing captured coord + optional draggable edit mode.
const LocationPreview = ({ lat, lng, accuracy, capturedAt, onCorrect }) => {
  const [editing, setEditing] = useState(false);
  const [tempPos, setTempPos] = useState({ lat, lng });

  if (!lat || !lng) return null;

  return (
    <div className="mt-2 rounded-lg overflow-hidden border border-[#E4E4E7] dark:border-[#27272A] bg-white dark:bg-[#18181B]">
      <div className="h-40 relative" data-testid="location-preview-map">
        <MapContainer
          center={[lat, lng]}
          zoom={16}
          className="w-full h-full"
          zoomControl={false}
          dragging={editing}
          scrollWheelZoom={editing}
          doubleClickZoom={editing}
          touchZoom={editing}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker
            position={editing ? [tempPos.lat, tempPos.lng] : [lat, lng]}
            icon={pinIcon}
            draggable={editing}
            eventHandlers={{
              dragend: (e) => {
                const p = e.target.getLatLng();
                setTempPos({ lat: p.lat, lng: p.lng });
              },
            }}
          />
          {editing && <MapClickHandler setPos={setTempPos} />}
        </MapContainer>
        {editing && (
          <div className="absolute top-2 left-2 right-2 bg-white/95 dark:bg-black/80 backdrop-blur-sm rounded px-2 py-1 text-[10px] text-center text-[#0A0A0A] dark:text-[#FAFAFA] font-semibold">
            Marker'ı sürükleyin veya haritaya tıklayın
          </div>
        )}
      </div>

      <div className="p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2 text-[10px]">
          <div>
            <div className="text-[#52525B] dark:text-[#A1A1AA] uppercase tracking-widest font-bold">Enlem, Boylam</div>
            <div className="text-[#0A0A0A] dark:text-[#FAFAFA] font-semibold">
              {(editing ? tempPos.lat : lat).toFixed(6)}, {(editing ? tempPos.lng : lng).toFixed(6)}
            </div>
          </div>
          {accuracy != null && (
            <div>
              <div className="text-[#52525B] dark:text-[#A1A1AA] uppercase tracking-widest font-bold">Doğruluk</div>
              <div className="text-[#0A0A0A] dark:text-[#FAFAFA] font-semibold">±{accuracy} m</div>
            </div>
          )}
          {capturedAt && (
            <div className="col-span-2">
              <div className="text-[#52525B] dark:text-[#A1A1AA] uppercase tracking-widest font-bold">Kaydedildi</div>
              <div className="text-[#0A0A0A] dark:text-[#FAFAFA] font-semibold">
                {new Date(capturedAt).toLocaleString("tr-TR")}
              </div>
            </div>
          )}
        </div>

        {!editing ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="location-correct-button"
            onClick={() => { setEditing(true); setTempPos({ lat, lng }); }}
            className="w-full border-[#E4E4E7] dark:border-[#27272A] h-8"
          >
            <Move className="w-3.5 h-3.5 mr-2" />Konumu Düzelt
          </Button>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => { setEditing(false); setTempPos({ lat, lng }); }}
              className="h-8 border-[#E4E4E7] dark:border-[#27272A]"
            >
              <X className="w-3.5 h-3.5 mr-1" />Vazgeç
            </Button>
            <Button
              type="button"
              size="sm"
              data-testid="location-confirm-button"
              onClick={() => {
                onCorrect?.(tempPos.lat, tempPos.lng);
                setEditing(false);
                toast.success("Konum güncellendi");
              }}
              className="h-8 bg-[#F59E0B] hover:bg-[#D97706] text-white"
            >
              <Check className="w-3.5 h-3.5 mr-1" />Onayla
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const MapClickHandler = ({ setPos }) => {
  const map = useMap();
  React.useEffect(() => {
    if (!map) return;
    const handler = (e) => setPos({ lat: e.latlng.lat, lng: e.latlng.lng });
    map.on("click", handler);
    return () => map.off("click", handler);
  }, [map, setPos]);
  return null;
};

export default LocationPreview;
